import { filter, map, values } from 'lodash';

import { SubscriptionType } from '@/types/pricingTypes/subscriptionType';
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

import { aclFeatures } from '@/lib/acl/aclFeatures';
import { PLANS } from '@/lib/pricing/plans';
import {
    ANALYZE_COMMUNITY,
    ANALYZE_CONTENT,
    ANALYZE_ENGAGEMENT,
    ANALYZE_IMPRESSIONS,
    ANALYZE_INDUSTRY_BENCHMARK,
    ANALYZE_MENTIONS,
    ANALYZE_PROFILE_ACTIVITY,
    ANALYZE_REACH,
    ANALYZE_REACH_IMPRESSIONS,
    ANALYZE_REELS,
    // Analytics
    ANALYZE_STORIES,
    ANALYZE_VIDEOS,
    CAPTIONS_LISTS,
    CONTENT_ALBUM_ANALYTICS,
    CONTENT_ALBUM_POSTS,
    CONTENT_ALBUM_STATS,
    CONTENT_ALBUMS,
    CONTENT_ALBUMS_COMPARATIVE_TABLE,
    // Content
    CONTENT_POSTS,
    DASHBOARD_CUSTOMIZATION,
    // Dashboard
    DASHBOARD_GENERATE_CUSTOM_REPORT,
    // Discover
    DISCOVER,
    INTEGRATIONS,
    LISTENING_COMPETITOR,
    LISTENING_COMPETITORS,
    // Listening
    LISTENING_FEED,
    LISTENING_FEEDS,
    LISTENING_HASHTAG,
    LISTENING_HASHTAGS,
    MANAGE_COMMENTS_TRACKER,
    MANAGE_DM_TRACKER,
    // Conversation
    MANAGE_MENTIONS_TRACKER,
    MEDIA,
    MEDIA_LIBRARY,
    // Reports
    REPORTS,
    REPORTS_ACCOUNTS,
    REPORTS_COMPETITORS,
    REPORTS_DASHBOARDS,
    REPORTS_EDIT,
    REPORTS_HASHTAGS,
    // Publishing
    SCHEDULER,
    SCHEDULER_COLLABORATION_NOTIFICATIONS,
    SCHEDULER_COLLABORATION_SETTINGS,
    SCHEDULER_FEED_VIEW,
    SUBSCRIPTION_INVOICES,
    // Settings
    USERS_ROLES
} from '@/lib/routes/privateRoutes';
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    THREADS,
    TIKTOK_BUSINESS,
    TWITTER,
    X,
    YOUTUBE
} from '@/lib/socialProfile';
import type { RoleType } from '@/lib/user/roles';
import { ADMIN_ROLE, MANAGER_ROLE } from '@/lib/user/roles';

type PageRightsType = Record<
    string,
    {
        permissions?: string[];
        plans?: (keyof typeof PLANS | SubscriptionType['product_key'])[];
        networks?: SocialProfileNetworkType[];
        roles?: RoleType[];
        acls?: Partial<Record<SocialProfileNetworkType, string[]>>;
        group?: boolean;
    }
>;

const pageRights: PageRightsType = {
    // Dashboard
    [DASHBOARD_GENERATE_CUSTOM_REPORT]: {
        permissions: [aclFeatures.report.white_label.read]
    },
    [DASHBOARD_CUSTOMIZATION]: {
        permissions: [aclFeatures.report.white_label.read]
    },
    // Analytics
    [ANALYZE_ENGAGEMENT]: { group: false },
    [ANALYZE_STORIES]: {
        networks: [INSTAGRAM],
        group: false
    },
    [ANALYZE_PROFILE_ACTIVITY]: {
        networks: [INSTAGRAM],
        group: false
    },
    [ANALYZE_VIDEOS]: {
        networks: [FACEBOOK],
        group: false
    },
    [ANALYZE_REACH]: {
        networks: [INSTAGRAM, FACEBOOK, LINKEDIN],
        group: false
    },
    [ANALYZE_REACH_IMPRESSIONS]: {
        networks: [INSTAGRAM, FACEBOOK, LINKEDIN, TWITTER],
        group: false
    },
    [ANALYZE_CONTENT]: {
        networks: [FACEBOOK, TWITTER, LINKEDIN, PINTEREST, TIKTOK_BUSINESS, YOUTUBE],
        group: false
    },
    [ANALYZE_IMPRESSIONS]: {
        networks: [TWITTER],
        group: false
    },
    [ANALYZE_INDUSTRY_BENCHMARK]: {
        networks: [FACEBOOK, INSTAGRAM],
        group: false
    },
    [ANALYZE_COMMUNITY]: {
        networks: [FACEBOOK, INSTAGRAM, LINKEDIN, PINTEREST, TWITTER, TIKTOK_BUSINESS, YOUTUBE],
        group: false
    },
    [ANALYZE_MENTIONS]: {
        networks: [INSTAGRAM],
        group: false,
        plans: map(
            filter(values(PLANS), (plan) => plan.features.includes(ANALYZE_MENTIONS)),
            'key'
        )
    },
    [ANALYZE_REELS]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    [LISTENING_COMPETITOR]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    [LISTENING_COMPETITORS]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    // media library
    [MEDIA_LIBRARY]: {
        networks: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
        group: false
    },
    // settings
    [USERS_ROLES]: {
        roles: [ADMIN_ROLE, MANAGER_ROLE]
    },
    [SUBSCRIPTION_INVOICES]: {
        roles: [ADMIN_ROLE] // remove access to subscription invoices for non-admins users
    },
    [INTEGRATIONS]: {
        roles: [ADMIN_ROLE] // remove access to subscription invoices for non-admins users
    },
    // Publishing
    [CAPTIONS_LISTS]: {
        networks: [FACEBOOK, INSTAGRAM, X, TWITTER, LINKEDIN, TIKTOK_BUSINESS, THREADS],
        group: false
    },
    [SCHEDULER]: {
        networks: [INSTAGRAM, FACEBOOK, X, TWITTER, LINKEDIN, TIKTOK_BUSINESS, THREADS],
        group: true
    },
    [SCHEDULER_FEED_VIEW]: {
        networks: [INSTAGRAM],
        group: false
    },
    [SCHEDULER_COLLABORATION_SETTINGS]: {
        networks: [INSTAGRAM, FACEBOOK, X, TWITTER, LINKEDIN, TIKTOK_BUSINESS, THREADS],
        permissions: [aclFeatures.publishing.external_collaboration.read],
        group: true
    },
    [SCHEDULER_COLLABORATION_NOTIFICATIONS]: {
        networks: [INSTAGRAM, FACEBOOK, X, TWITTER, LINKEDIN, TIKTOK_BUSINESS, THREADS],
        permissions: [aclFeatures.publishing.external_collaboration.read],
        group: true
    },
    // Listening
    [LISTENING_HASHTAG]: {
        networks: [INSTAGRAM],
        group: false
    },
    [LISTENING_HASHTAGS]: {
        networks: [INSTAGRAM],
        group: false
    },
    [LISTENING_FEED]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    [LISTENING_FEEDS]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    // Manage
    [MANAGE_MENTIONS_TRACKER]: {
        networks: [INSTAGRAM],
        group: false
    },
    [MANAGE_COMMENTS_TRACKER]: {
        networks: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN],
        group: false
    },
    [MANAGE_DM_TRACKER]: {
        networks: [INSTAGRAM],
        group: false,
        plans: map(
            filter(Object.values(PLANS), (plan) => plan.features.includes(MANAGE_DM_TRACKER)),
            'key'
        )
    },
    // Reports
    [REPORTS]: { group: false },
    [REPORTS_ACCOUNTS]: { group: false },
    [REPORTS_COMPETITORS]: { group: false },
    [REPORTS_HASHTAGS]: { group: false },
    [REPORTS_EDIT]: { group: false },
    [REPORTS_DASHBOARDS]: {
        group: false
    },
    // Content
    [CONTENT_POSTS]: { group: false },
    [CONTENT_ALBUMS]: { group: true },
    [CONTENT_ALBUM_POSTS]: { group: true },
    [CONTENT_ALBUM_STATS]: { group: true },
    [CONTENT_ALBUM_ANALYTICS]: { group: true },
    [CONTENT_ALBUMS_COMPARATIVE_TABLE]: { group: true },
    [MEDIA]: { group: false },
    // Discover
    [DISCOVER]: { group: false }
};

export default pageRights;
